import { useEffect, useState } from 'react';
import ReactApexChart from 'react-apexcharts';
import { getSireReviewByRequestor, SireReviewByRequestor } from '@/services/dashboardService';
import { getCurrentMonth } from '@/utils/date';

import { DataStatus } from './dataStatus';
import { LoadingSpinner } from './LoadingSpinner';
import MonthSelector from './MonthSelector';
import './dashboard.css';
import chartColors from './chartColors';

const {red, green, blue} = chartColors;

const ChartSireReviewByRequestor = () => {
    const {year: currentYear, month: currentMonth} = getCurrentMonth();

    const [requestorNames, setRequestorNames] = useState<string[]>([]);
    const [inProgressReviews, setInProgressReviews] = useState<number[]>([]);
    const [recommendedReviews, setRecommendedReviews] = useState<number[]>([]);
    const [notRecommendedReviews, setNotRecommendedReviews] = useState<number[]>([]);
    const [dataStatus, setDataStatus] = useState<DataStatus>(DataStatus.LOADING);
    const [month, setMonth] = useState<number>(currentMonth);
    const [year, setYear] = useState<number>(currentYear);

    const getData = async (month: number, year: number) => {
        setDataStatus(DataStatus.LOADING)
        const response = await getSireReviewByRequestor(month, year);
        if (!response.data){
            setDataStatus(DataStatus.ERROR);
            return;
        }
        if (response.data.length === 0){
            setDataStatus(DataStatus.EMPTY);
            return;
        }
        const originalData = response.data as SireReviewByRequestor[];
        const transformedData = transformData(originalData);
        setRequestorNames(transformedData.requestors);
        setInProgressReviews(transformedData.inProgress);
        setRecommendedReviews(transformedData.recommended);
        setNotRecommendedReviews(transformedData.notRecommended);
        setDataStatus(DataStatus.OK);
    };

    const transformData = (originalData: SireReviewByRequestor[]) => {
        const requestors: string[] = [];
        const inProgress: number[] = [];
        const recommended: number[] = [];
        const notRecommended: number[] = [];
        originalData.map((requestor) => {
            requestors.push(requestor.requestor);
            inProgress.push(requestor.sireReviewInProgress);
            recommended.push(requestor.reviewResultRecommended);
            notRecommended.push(requestor.reviewResultNotRecommended);
        });
        return { requestors, inProgress, recommended, notRecommended };
    };

    useEffect(() => {
        getData(currentMonth, currentYear);
    }, []);

    useEffect(() => {
        getData(month, year);
    }, [month, year]);

    const series = [
        {
            name: 'In Progress',
            data: inProgressReviews,
        }, {
            name: 'Recommended',
            data: recommendedReviews,
        }, {
            name: 'Not Recommended',
            data: notRecommendedReviews,
        },
    ];

    const chartOptions: ApexCharts.ApexOptions = {
        chart: {
            type: 'bar',
            stacked: true,
        },
        plotOptions: {
            bar: {
                horizontal: false,
                dataLabels: {
                    position: 'top',
                    total: {
                        enabled: true,
                    },
                },
            },
        },
        series,
        xaxis: {
            categories: requestorNames,
        },
        colors: [blue, green, red],
    };

    return (
        <div className='card card-pie'>
            <h2 className='card-title'>SIRE Reviews by Requestor</h2>
            <div>
                <MonthSelector month={month} year={year} onChange={(selectedMonth, selectedYear) => { setMonth(selectedMonth); setYear(selectedYear) }} />
                {(dataStatus === DataStatus.LOADING) && <LoadingSpinner />}
            </div>
            {(dataStatus === DataStatus.EMPTY || dataStatus === DataStatus.ERROR) && <div>No data to display</div>}
            {(dataStatus === DataStatus.OK || dataStatus === DataStatus.LOADING) && <ReactApexChart type="bar" options={chartOptions} series={series} height={420} />}
        </div>
    );
};

export default ChartSireReviewByRequestor;
